/**
 * CalendarEventCard — card di un singolo evento nel Calendario
 * (badge tipo/stato, link lead, azioni nota/modifica/elimina, storico note).
 * ★ Fase 59.P2 — estratto da CalendarPage.jsx. Comportamento e testid invariati.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Edit, Trash2, MessageSquarePlus, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { fmtDateTime } from '../../lib/constants';
import { typeLabel, formatTime } from './calendarUtils';
import i18n from '../../i18n';

export function CalendarEventCard({ ev, onAddNote, onEdit, onDelete, onDeleteNote }) {
  return (
    <Card
      className={`p-3 md:p-4 ${ev.status === 'done' ? 'opacity-60' : ''}`}
      data-testid={`calendar-event-${ev.id}`}
    >
      {/* Top row */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge variant="outline" className="text-[10px] shrink-0">
              {typeLabel(ev.type)}
            </Badge>
            {ev.status === 'done' && (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] shrink-0">
                {i18n.t('calendarX.done', 'Completato')}
              </Badge>
            )}
            <span className="text-xs text-muted-foreground tabular-nums shrink-0 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTime(ev.event_at)}
            </span>
          </div>
          <div className="font-medium text-sm md:text-base break-words">{ev.title}</div>
          <Link
            to={`/app/leads/${ev.lead_id}`}
            className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1 mt-1"
            data-testid={`calendar-event-open-lead-${ev.id}`}
          >
            {ev.lead_name || 'Apri lead'} <ExternalLink className="w-3 h-3" />
          </Link>
        </div>

        <div className="flex gap-1 shrink-0">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => onAddNote(ev)}
            data-testid={`calendar-event-add-note-${ev.id}`}
            title={i18n.t('calendarX.addNote', 'Aggiungi nota')}
          >
            <MessageSquarePlus className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => onEdit(ev)}
            data-testid={`calendar-event-edit-${ev.id}`}
            title="Modifica"
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-red-600 hover:text-red-700"
            onClick={() => onDelete(ev.id)}
            data-testid={`calendar-event-delete-${ev.id}`}
            title={i18n.t('common.delete', 'Elimina')}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Original notes field */}
      {ev.notes && (
        <div className="mt-2 text-xs italic text-muted-foreground bg-[var(--surface-2)] rounded p-2">
          {ev.notes}
        </div>
      )}

      {/* Notes history */}
      {(ev.notes_history || []).length > 0 && (
        <div className="mt-3 border-t pt-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
            Trascrizioni / Note ({ev.notes_history.length})
          </div>
          <div className="space-y-1.5">
            {ev.notes_history.map((n) => (
              <div
                key={n.id}
                className="text-xs bg-[var(--surface-2)] rounded p-2"
                data-testid={`event-note-${n.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="font-medium text-[11px]">{n.created_by || '—'}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      {fmtDateTime(n.created_at)}
                    </span>
                    <button
                      type="button"
                      onClick={() => onDeleteNote(ev.id, n.id)}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`event-note-delete-${n.id}`}
                      title={i18n.t('calendarX.deleteNote', 'Elimina nota')}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="whitespace-pre-wrap break-words">{n.text}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
